import React, { ChangeEventHandler, useRef, useState } from "react";
import PropTypes from "prop-types";

import { format, isValid, parse } from "date-fns";
import FocusTrap from "focus-trap-react";
import { DayPicker } from "react-day-picker";
import { usePopper } from "react-popper";
import { useField, useFormikContext } from "formik";

import "react-day-picker/dist/style.css";
import "../../../styles/datePicker.scss";

const DatePicker = (props) => {
  const classNames = ["date-picker"];
  const { dateFormat, label, defaultValue } = props;
  const [field, meta] = useField(props);
  const formikContext = useFormikContext();
  const [selected, setSelected] = useState(
    defaultValue || meta?.initialValue || undefined
  );
  const [inputValue, setInputValue] = useState(
    selected ? format(new Date(selected), dateFormat) : undefined
  );
  const [isPopperOpen, setIsPopperOpen] = useState(false);

  const popperRef = useRef(null);
  const [popperElement, setPopperElement] = useState(null);

  const popper = usePopper(popperRef.current, popperElement, {
    placement: "bottom-start",
    strategy: "fixed",
  });

  const closePopper = () => {
    setIsPopperOpen(false);
  };

  const handleInputChange = (e) => {
    setInputValue(e.currentTarget.value);
    const date = parse(e.currentTarget.value, dateFormat, new Date());
    if (isValid(date)) {
      setSelected(date);
      return;
    }

    setSelected(undefined);
  };

  const handleDaySelect = (date) => {
    setSelected(date);

    if (date) {
      formikContext.setFieldValue(field?.name, date);
      setInputValue(format(date, dateFormat));
      closePopper();
      return;
    }

    formikContext.setFieldValue(field?.name, null);
    setInputValue("");
  };

  const handleInputClick = (e) => {
    if (isPopperOpen) return;
    setIsPopperOpen(true);
  };

  if (meta?.error) {
    classNames.push("date-picker--error");
  }

  return (
    <div className={classNames.join(" ")}>
      <div className="date-picker-container" ref={popperRef}>
        {label && <label className="date-picker-label">{label}</label>}
        <input
          className="date-picker-input"
          size={12}
          type="text"
          placeholder={format(new Date(), dateFormat)}
          value={inputValue}
          onChange={handleInputChange}
          onClick={handleInputClick}
        />
        <div className="date-picker-footer">
          {meta?.error && (
            <div className="date-picker-footer-error">{meta.error}</div>
          )}
        </div>
      </div>
      {isPopperOpen && (
        <FocusTrap
          active
          focusTrapOptions={{
            initialFocus: false,
            allowOutsideClick: true,
            clickOutsideDeactivates: true,
            onDeactivate: closePopper,
          }}
        >
          <div
            className="date-picker-popper"
            tabIndex={-1}
            style={popper.styles.popper}
            {...popper.attributes.popper}
            ref={setPopperElement}
            role="dialog"
            aria-label="DayPicker calendar"
          >
            <DayPicker
              initialFocus={isPopperOpen}
              mode="single"
              defaultMonth={selected}
              selected={selected}
              onSelect={handleDaySelect}
            />
          </div>
        </FocusTrap>
      )}
    </div>
  );
};

DatePicker.propTypes = {
  label: PropTypes.string,
  dateFormat: PropTypes.string.isRequired,
};

export default DatePicker;
